import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HeadlineLarge, HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import TextField from '@shared/ui/atoms/TextField';
import AccessRequired from './components/AccessRequired';
import { useParams } from 'react-router';
import { TeamWithCount } from '@shared/models/team.models';
import {
  getTeamMembers,
  updateTeamAssignedDepartmentsRequest,
  updateTeamAssignedOfficesRequest,
  updateTeamRequest
} from '@shared/services/TeamService';
import UserList from '@shared/ui/organisms/user-list/UserList';
import { redirectTo, selectCurrentUser } from '@shared/store/auth.duck';
import { SecondaryButton } from '@shared/ui/atoms/buttons/SecondaryButton';
import { selectIsAddMemberDialogOpen } from '@shared/store/addMemberDialog.duck';
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import { getNotificationMessage, NotificationMessages, redirectWithNotification } from '@shared/models/notification.models';
import { failureNotification, successNotification } from '@shared/store/notification.duck';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { canUserEditTeam, canUserMoveUsersIntoDifferentTeams } from "@shared/utils/AccessControl";
import { DialogIdentifiers, openDialog, openDialogWithPayload } from '@shared/store/dialog.duck';
import { Column, Row } from '@shared/ui/atoms/StructuralLayout';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { useTranslation } from "react-i18next";
import { loadConfig, selectAllTeams } from "@shared/store/config.duck";
import { OfficeMultiSelectDropDownList } from '@shared/ui/organisms/OfficeMultiSelectDropDownList';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import styled from "styled-components";
import { DepartmentsMultiSelectDropDownList } from '@shared/ui/organisms/DepartmentsMultiSelectDropDownList';
import { Department } from "@features/whereabouts/pages/departments/data/departments.models";
import { selectAllDepartments } from "@features/whereabouts/pages/departments/data/departments.duck";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export default function EditTeamPage() {
  const params: any = useParams();
  return <EditTeamView teamId={params.teamId} />;
}

interface EditTeamViewProps {
  teamId: string;
  // When set, the view is embedded in another page: going back, saving or a
  // load failure return control to the host instead of navigating away.
  onClose?: () => void;
}

export function EditTeamView({ teamId, onClose }: EditTeamViewProps) {
  const dispatch = useDispatch();
  const current = useSelector(selectCurrentUser);
  const teams = useSelector(selectAllTeams);
  const addMemberDialogOpenState = useSelector(selectIsAddMemberDialogOpen);
  const departments = useSelector(selectAllDepartments);
  const [selectedTeam, setSelectedTeam] = useState<TeamWithCount | undefined>(undefined);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [selectedOfficeIds, setSelectedOfficeIds] = useState<number[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<number[]>([]);
  const isTeamEditable = useMemo(() => canUserEditTeam(current, selectedTeam), [current, selectedTeam]);
  const canMoveUsers = useMemo(() => canUserMoveUsersIntoDifferentTeams(current), [current]);
  const {t} = useTranslation();

  const failAndReturn = (message: NotificationMessages) => {
    if (onClose) {
      dispatch(failureNotification(getNotificationMessage(message) ?? 'Unknown error'));
      onClose();
    } else {
      redirectWithNotification(message, '/manage/teams-and-users');
    }
  };

  useEffect(() => {
    if (!addMemberDialogOpenState && isDialogOpen) {
      const loadTeamDetails = async () => {
        if (selectedTeam) {
          setLoading(true);
          try {
            const teamMembers = await getTeamMembers(selectedTeam.id);
            setTeamMembers(teamMembers);
          } catch (err: any) {
            console.error(err)
            failAndReturn(NotificationMessages.errorLoadingTeam)
          } finally {
            setLoading(false)
          }
        }
      }
      setIsDialogOpen(false);
      loadTeamDetails();
    }
  }, [selectedTeam, addMemberDialogOpenState, isDialogOpen])

  useEffect(() => {
    const team = teams.find(t => t.id === teamId);
    if (team) {
      setLoading(true);
      setSelectedTeam(team);
      setTeamName(team?.name ?? '');
      setSelectedOfficeIds(team?.officeIds ?? [])
      setSelectedDepartmentIds(team?.departmentIds ?? [])
      setLoading(true);
      getTeamMembers(team.id)
        .then(teamMembers => {
          setTeamMembers(teamMembers);
        })
        .then(() => setLoading(false))
        .catch(() => failAndReturn(NotificationMessages.errorLoadingTeam))
    } else if (teams.length > 0) {
      console.error('Error, there has been an error finding the team id')
      failAndReturn(NotificationMessages.unknownError)
    } else {
      setLoading(true);
      dispatch(loadConfig(['teams']));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams, teamId, dispatch]);

  const reloadTeamMembers = () => {
    if (!selectedTeam) return;
    setLoading(true);
    getTeamMembers(selectedTeam.id)
      .then(setTeamMembers)
      .catch(() => failAndReturn(NotificationMessages.errorLoadingTeam))
      .finally(() => setLoading(false));
  };

  const openAddTeamMemberDialog = () => {
    dispatch(openDialogWithPayload({
      activeDialog: DialogIdentifiers.AddTeamMemberDialog,
      payload: {
        team: selectedTeam,
        // Refresh the member list once the dialog closes.
        onClose: reloadTeamMembers,
      },
    }));
  };

  const openDeleteTeamDialog = () => {
    if (!selectedTeam) return;
    dispatch(openDialogWithPayload({
      activeDialog: DialogIdentifiers.DeleteTeamDialog,
      payload: {team: selectedTeam},
    }));
  }

  const saveChanges = async () => {
    if (selectedTeam) {
      setLoading(true);
      try {
        await updateTeamRequest(selectedTeam.id, teamName);
        await updateTeamAssignedOfficesRequest(selectedTeam.id, selectedOfficeIds);

        if (departments.length > 0) {
          await updateTeamAssignedDepartmentsRequest(selectedTeam.id, selectedDepartmentIds);
        }

        dispatch(loadConfig(['teams', 'departments']));
        dispatch(successNotification('Team updated'));
        if (onClose) {
          onClose();
        } else {
          dispatch(redirectTo('/manage/teams-and-users'));
        }
      } catch {
        dispatch(failureNotification('Team failed to update'));
      } finally {
        setLoading(false);
      }
    }
  }

  const onOfficesSelected = (offices: OfficeEntity[]) => {
    const officeIds = offices.map(o => o.id);
    setSelectedOfficeIds(officeIds);
  }

  const onDepartmentsSelected = (departments: Department[]) => {
    setSelectedDepartmentIds(departments.map(d => d.id));
  }

  return (
    <React.Fragment>
      {loading && <LoadingSpinner fullScreen={true}/>}
      <div className="management">

        <GoBackButton onClick={onClose}/>
        <HeadlineLarge className="management__title">{t('edit-team.title')}</HeadlineLarge>
        <AccessRequired visible={!isTeamEditable && !canMoveUsers} companyAdminsRequired={true} teamAdminsRequired={true}/>

        <Column className="management__row">
          <TextField dataTest="editTeamPage-teamName-input" label={'edit-team.team-name'} value={teamName} onChange={setTeamName}
                     disabled={!isTeamEditable}/>

          <SelectOfficesWrapper>
            <OfficeMultiSelectDropDownList label={'edit-team.select-office-description'}
                                           selectedOfficeIds={selectedOfficeIds}
                                           onSelect={onOfficesSelected}
                                           disabled={false}/>
          </SelectOfficesWrapper>

          {departments.length > 0 && <SelectOfficesWrapper style={{marginBottom: 48}}>
            <DepartmentsMultiSelectDropDownList label={'edit-team.select-departments-description'}
                                                selectedDepartmentIds={selectedDepartmentIds}
                                                onSelect={onDepartmentsSelected}
                                                disabled={false}/>
          </SelectOfficesWrapper>}

          {isTeamEditable ?
            <SecondaryButton dataTest="editTeamPage-save" size={ButtonSize.Large}
                             text="button.save-changes" fullWidth={false}
                             click={saveChanges}/> : null}
        </Column>


        <div className={`${canMoveUsers ? '' : 'management__disabled'}`.trim()}>
          <div className="editTeam__membersHeader">
            <HeadlineSmall className="management__row">{t('members')}</HeadlineSmall>
            <Row className="management__row" style={{gap: 8}}>
              <OutlineButton dataTest="editTeamPage-addMultipleUsers-button"
                             size={ButtonSize.Medium}
                             text="manage-users-page.add-multiple-users"
                             disabled={!canMoveUsers}
                             click={() => dispatch(openDialog(DialogIdentifiers.AddMultipleUsersInfo))}/>
              <OutlineButton dataTest="editTeamPage-addTeamMember-button"
                             size={ButtonSize.Medium}
                             text="button.add-team-member"
                             disabled={!canMoveUsers}
                             style={{paddingLeft: 26, paddingRight: 26}}
                             click={openAddTeamMemberDialog}/>
            </Row>
          </div>
          <UserList users={teamMembers} edittable={canMoveUsers}/>
        </div>

        {isTeamEditable ?
          <div className="management__loneControl">
            <IconButton className="editTeam__deleteButton" color="darkGrey" icon={IconTypes.Bin} text="button.delete-team"
                        onClick={() => openDeleteTeamDialog()}/>
          </div> : null}

      </div>
    </React.Fragment>
  );
}

const SelectOfficesWrapper = styled.div`
  margin-bottom: 24px;
`
